import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCompany } from "@/hooks/useCompany";

export type CompanyUsageStats = {
  articles_created_this_month: number;
  websites_created: number;
  media_assets_used: number;
  team_members_used: number;
  ai_credits_used: number;
};

async function fetchCompanyUsage(companyId: string): Promise<CompanyUsageStats> {
  const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();

  const { count: articlesCount, error: articlesError } = await supabase
    .from("articles")
    .select("id", { count: "exact", head: true })
    .eq("company_id", companyId)
    .gte("created_at", monthStart);
  
  const { count: websitesCount, error: websitesError } = await supabase
    .from("websites")
    .select("id", { count: "exact", head: true })
    .eq("company_id", companyId);
  
  const { count: mediaCount, error: mediaError } = await supabase
    .from("media_assets")
    .select("id", { count: "exact", head: true })
    .eq("company_id", companyId);
  
  const { count: membersCount, error: membersError } = await supabase
    .from("company_members")
    .select("user_id", { count: "exact", head: true })
    .eq("company_id", companyId);
  
  const { data: creditRows } = await (supabase as any)
    .from("credit_transactions")
    .select("delta")
    .eq("company_id", companyId)
    .lt("delta", 0)
    .gte("created_at", monthStart);

  if (articlesError || websitesError || mediaError || membersError) {
    throw articlesError || websitesError || mediaError || membersError;
  }

  const aiCreditsUsed = (creditRows ?? []).reduce((sum, row) => sum + Math.abs(Number(row.delta ?? 0)), 0);

  return {
    articles_created_this_month: articlesCount ?? 0,
    websites_created: websitesCount ?? 0,
    media_assets_used: mediaCount ?? 0,
    team_members_used: membersCount ?? 0,
    ai_credits_used: aiCreditsUsed,
  };
}

export function useCompanyUsage() {
  const { currentCompanyId } = useCompany();
  
  const { data: usage = {
    articles_created_this_month: 0,
    websites_created: 0,
    media_assets_used: 0,
    team_members_used: 0,
    ai_credits_used: 0,
  }, isLoading } = useQuery({
    queryKey: ["company-usage", currentCompanyId],
    queryFn: () => fetchCompanyUsage(currentCompanyId!),
    enabled: !!currentCompanyId,
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  return { usage, isLoading };
}
